import * as React from "react";
import { Button } from "./Button";
import { useAuth } from "./AuthProvider";
import { Brand } from "./Brand";

/**
 * TokenScreen — setting a password from an emailed link: a password RESET, or accepting an
 * INVITE. Both are the same form against two endpoints, which is why v1 served both from one
 * function and why this does too.
 *
 * ─── Why this exists ────────────────────────────────────────────────────────────────────────
 *
 * PASSWORD RESET WAS COMPLETELY BROKEN, on both nodes, for every role. Measured end to end on
 * node10, 2026-08-31:
 *
 *   1. auth_recovery.go emails `https://<node fqdn>/#/reset?token=<token>`.
 *   2. `GET /` 302s to `/admin/` — v2Default, because an unauthenticated visitor has no role to
 *      redirect by.
 *   3. The browser carries the fragment across the redirect, so the person arrives at
 *      `https://<node>/admin/#/reset?token=…`.
 *   4. The v2 admin console had no `/reset` route and never called `/auth/password/reset`.
 *      Confirmed against the SERVED bundle, not the source: grep for "reset-request" in the
 *      deployed JS returned nothing.
 *   5. So they get the sign-in form. The token sits unused in the address bar.
 *
 * Nothing reported a fault anywhere along that path. The mail is sent, the link resolves, the
 * page renders, the server answers 200. v1 handled it — `{re:/^\/reset(\?.*)?$/, fn:viewTokenForm}`
 * at index.html:1002, and `/invite` on the line below — and it stopped working the moment `/`
 * was flipped from v1 to v2, which is a change nobody would connect to account recovery. The
 * only way to find it was to follow the link a user actually receives.
 *
 * ─── Why it lives in @ozpanel/ui, beside LoginScreen ────────────────────────────────────────
 *
 * The flow is PRE-AUTH and ROLE-LESS: it needs a token and a new password, nothing else. It
 * therefore must not live inside one console — and it cannot, because the redirect that lands a
 * visitor somewhere happens before anyone knows who they are, so a mailbox user resetting their
 * password arrives at /admin/ like everybody else.
 *
 * A fragment never reaches the server, so the server cannot route on it either. Whichever
 * console the visitor lands in has to handle it, which means all three — one implementation,
 * mounted wherever LoginScreen is.
 */

/** Which flow the link is for. They differ only in endpoint and wording. */
export type TokenPurpose = "reset" | "invite";

export interface TokenScreenProps {
  purpose: TokenPurpose;
  token: string;
  /** Shown after success, to send the person back to a sign-in form. */
  onDone?: () => void;
}

const COPY: Record<TokenPurpose, { title: string; subtitle: string; action: string }> = {
  reset: {
    title: "Choose a new password",
    subtitle: "This link works once. Setting a password signs out every other session.",
    action: "Set new password",
  },
  invite: {
    title: "Set your password",
    subtitle: "Finish setting up your account by choosing a password.",
    action: "Set password and continue",
  },
};

/**
 * NOTE on the two literals below. The endpoint used to live in the table above as `path`, and
 * the call was `apiFetch(copy.path)`. That works, and it made both endpoints invisible to every
 * tool that measures what the consoles reach — the coverage scan reads call sites, and a
 * variable is not a path. They showed as unreached while a browser drill proved them working,
 * which is the same false gap that would hide a real one. A literal at the point of use costs
 * one branch and keeps the measurement honest.
 */


export function TokenScreen({ purpose, token, onDone }: TokenScreenProps) {
  const { client } = useAuth();
  const copy = COPY[purpose];
  const [password, setPassword] = React.useState("");
  const [confirm, setConfirm] = React.useState("");
  const [error, setError] = React.useState<string | null>(null);
  const [done, setDone] = React.useState<string | null>(null);
  const [submitting, setSubmitting] = React.useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    // Checked here as well as by the server. The token is SPENT on any redeem attempt — the
    // handler says so ("This link has now been used — request another") — so letting a typo'd
    // confirmation reach it would burn the link on a mistake the browser could see.
    if (password !== confirm) {
      setError("Those two passwords are not the same.");
      return;
    }
    setSubmitting(true);
    try {
      const r =
        purpose === "reset"
          ? await client.apiFetch<{ reset?: boolean; sessions_revoked?: number; note?: string }>(
              "/auth/password/reset",
              { method: "POST", body: JSON.stringify({ token, password }) },
            )
          : await client.apiFetch<{ reset?: boolean; sessions_revoked?: number; note?: string }>(
              "/auth/invite/accept",
              { method: "POST", body: JSON.stringify({ token, password }) },
            );
      // The server's note is the whole sentence, INCLUDING the revoked count when there is one —
      // it swaps in "Signed out N other session(s) — if you were not signed in elsewhere,
      // somebody else was", which is the point of reporting the number at all.
      //
      // Appending the count as well printed it twice, which the live drill showed and a reading
      // of the handler would also have shown. One writer for that sentence, and it is the server:
      // it is the only side that knows whether N is a reassurance or a warning.
      setDone(r?.note ?? "Your password is set.");
    } catch (err) {
      setError(err instanceof Error && err.message ? err.message : "That link could not be used.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="grid min-h-dvh place-items-center p-6">
      <div className="w-full max-w-[390px]">
        <div className="mb-7 text-center">
          <div className="mb-4 flex justify-center">
            <Brand />
          </div>
          <h1 className="text-[19px] font-[680] tracking-[-0.02em]">{done ? "All set" : copy.title}</h1>
          <p className="mt-1.5 text-[14px] text-muted">{done ?? copy.subtitle}</p>
        </div>

        {done ? (
          <Button variant="accent" className="w-full py-3 text-[15px]" onClick={() => onDone?.()}>
            Go to sign in
          </Button>
        ) : (
          <form
            onSubmit={onSubmit}
            className="rounded-panel border border-line bg-gradient-to-b from-surface to-bg2 p-[26px]"
          >
            {error && (
              <p className="mb-4 rounded-lg border border-[color-mix(in_oklab,var(--ozp-crit)_40%,transparent)] bg-critsoft px-3 py-2.5 text-[13px] text-crit">
                {error}
              </p>
            )}
            <TokenField label="New password" id="np" value={password} onChange={setPassword} />
            <TokenField label="Repeat it" id="np2" value={confirm} onChange={setConfirm} />
            <Button type="submit" variant="accent" disabled={submitting} className="mt-1 w-full py-3 text-[15px]">
              {submitting ? "Saving…" : copy.action}
            </Button>
          </form>
        )}
      </div>
    </main>
  );
}

function TokenField({
  label,
  id,
  value,
  onChange,
}: {
  label: string;
  id: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div className="mb-[15px]">
      <label htmlFor={id} className="mb-[7px] block text-[12px] font-medium text-muted">
        {label}
      </label>
      <input
        id={id}
        type="password"
        required
        autoComplete="new-password"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-[9px] border border-line2 bg-bg px-[13px] py-[11px] text-[15px] text-fg transition-[border-color,box-shadow] outline-none focus:border-accent focus:shadow-[0_0_0_3px_var(--ozp-accent-soft)]"
      />
    </div>
  );
}

/**
 * readTokenLink reads a reset/invite link out of the URL.
 *
 * It looks in the FRAGMENT first, because that is the form the server emails
 * (`/#/reset?token=…`) and a fragment never reaches the server — so nothing but the browser can
 * act on it. The query string is accepted too, so a future link that uses a real path keeps
 * working without a second reader.
 *
 * Returns null for anything else, which is the ordinary case: the caller then renders its login
 * screen exactly as before.
 */
export function readTokenLink(loc: { hash: string; search: string } = window.location):
  | { purpose: TokenPurpose; token: string }
  | null {
  for (const raw of [loc.hash.replace(/^#/, ""), loc.search]) {
    if (!raw) continue;
    const q = raw.indexOf("?");
    const path = q >= 0 ? raw.slice(0, q) : raw;
    const params = new URLSearchParams(q >= 0 ? raw.slice(q + 1) : "");
    const token = params.get("token");
    if (!token) continue;
    if (path === "/reset" || path === "reset") return { purpose: "reset", token };
    if (path === "/invite" || path === "invite") return { purpose: "invite", token };
  }
  return null;
}
